import React, { useEffect, useId, useRef, useState } from 'react';
import { DecisionBar, Journey, Layout, Rail, Surface, TaskButton, Work } from './creditReviewStyles';

export interface PCReviewSection {
  id: string;
  title: string;
  description: string;
  detail: string;
  content: React.ReactNode;
  actions?: React.ReactNode;
}

interface Props {
  assetLabel?: string;
  title: string;
  description: string;
  journey: readonly (readonly [string, string])[];
  /** Only set when an existing persisted state identifies a lifecycle step. */
  currentStep?: number;
  summary?: React.ReactNode;
  status?: React.ReactNode;
  sections: PCReviewSection[];
  editor?: React.ReactNode;
  actions?: React.ReactNode;
  actionTitle: string;
  actionDetail: string;
  /** Section to open when the page is reached for a specific piece of work. */
  openSection?: string;
}

/** Shared credit presentation. No fetching, authority inference or business transitions. */
const CreditReviewWorkspace: React.FC<Props> = p => {
  const [selected, setSelected] = useState(p.sections[0]?.id);
  const current = p.sections.find(section => section.id === selected) || p.sections[0];
  const work = useRef<HTMLElement>(null);
  const editor = useRef<HTMLDivElement>(null);
  const workId = useId();
  const editing = Boolean(p.editor);
  useEffect(() => { if (p.openSection) setSelected(p.openSection); }, [p.openSection]);
  // An editor that opens above the fold looks like a button that did nothing: show it and focus it.
  useEffect(() => {
    if (!editing || !editor.current) return;
    editor.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
    editor.current.querySelector<HTMLElement>('input, textarea, select')?.focus({ preventScroll: true });
  }, [editing]);
  if (!current) return null;

  return <Surface aria-label={`${p.assetLabel || 'Private Credit'} ${p.title}`}>
    <Journey aria-label={`${p.title} workflow`}>
      {p.journey.map(([title, detail], i) => <li key={title} aria-current={p.currentStep === i + 1 ? 'step' : undefined}>
        <span className="step" aria-hidden="true">{i + 1}</span><div><strong>{title}</strong><small>{detail}</small></div>
      </li>)}
    </Journey>
    {p.summary && <div style={{ marginBottom: 18 }}>{p.summary}</div>}
    <Layout>
      <Rail aria-label={`${p.title} review sections`}>
        <div className="heading"><h2>{p.title}</h2><p>{p.description}</p></div>
        <div className="tasks">{p.sections.map((section, i) => <TaskButton type="button" key={section.id}
          aria-pressed={current.id === section.id} aria-controls={workId} onClick={() => {
            setSelected(section.id);
            if (window.matchMedia('(max-width: 1000px)').matches) work.current?.scrollIntoView({ block: 'start' });
          }}>
          <span className="category">{String(i + 1).padStart(2, '0')} · {p.title}</span>
          <strong>{section.title}</strong><span className="reason">{section.detail}</span>
        </TaskButton>)}</div>
      </Rail>
      <Work id={workId} ref={work} aria-label={current.title}>
        <div className="work-heading"><div><h2>{current.title}</h2><p>{current.description}</p></div>{p.status}</div>
        {p.editor && <div className="editor" ref={editor}>{p.editor}</div>}
        {/* Mount each section once; changing the rail preserves forms, selections and disclosures. */}
        {p.sections.map(section => <div key={section.id} hidden={current.id !== section.id}>{section.content}</div>)}
        <DecisionBar aria-label={`${p.title} actions`}>
          <div className="state"><strong>{p.actionTitle}</strong><span>{p.editor ? 'Finish or cancel the open editor before starting another action.' : p.actionDetail}</span></div>
          {current.actions || p.actions}
        </DecisionBar>
      </Work>
    </Layout>
  </Surface>;
};

export default CreditReviewWorkspace;
